import { useEffect, useMemo, useState } from 'react'
import { Box, Card, Stack, Typography } from '@mui/material'
import { eventService } from '../services/eventService'
import type { Session } from '../data/types'
import PageContainer from '../components/ui/PageContainer'
import { EmptyState, LoadingState } from '../components/ui/States'
import { formatDayLabel } from '../utils/format'
import { resolveAssetUrl } from '../services/assetUrl'

/** True if the URL points at a directly-playable video file (an uploaded
 *  recording served from our object storage, or a direct video-file link). */
function isDirectVideo(url: string): boolean {
  if (url.startsWith('/api/assets/')) return true
  return /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i.test(url)
}

/** Normalize a YouTube/Vimeo URL to an embeddable src (else null). */
function toEmbed(url: string): string | null {
  try {
    const u = new URL(url)
    const host = u.hostname.replace(/^www\./, '')
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1).split('/')[0]
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null
    }
    if (host.endsWith('youtube.com')) {
      if (u.pathname.startsWith('/watch')) {
        const id = u.searchParams.get('v')
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null
      }
      if (u.pathname.startsWith('/embed/')) return url
    }
    if (host.endsWith('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).find((p) => /^\d+$/.test(p))
      return id ? `https://player.vimeo.com/video/${id}` : null
    }
  } catch {
    return null
  }
  return null
}

/** On-demand recordings hub: sessions that have a recording. */
export default function WatchPage() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    eventService
      .getSessions()
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setLoading(false))
  }, [])

  const withRecordings = useMemo(
    () =>
      sessions
        .filter((s) => s.recordingUrl)
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [sessions],
  )

  if (loading) return <LoadingState />
  if (withRecordings.length === 0)
    return (
      <PageContainer title="Watch" subtitle="On-demand session recordings">
        <EmptyState
          title="No recordings yet"
          description="Session recordings will appear here after the event."
        />
      </PageContainer>
    )

  return (
    <PageContainer title="Watch" subtitle="On-demand session recordings">
      <Stack spacing={3}>
        {withRecordings.map((s) => {
          const url = s.recordingUrl as string
          const direct = isDirectVideo(url)
          const embed = direct ? null : toEmbed(url)
          return (
            <Card key={s.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {s.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {formatDayLabel(s.startsAt)}
                {s.track ? ` · ${s.track}` : ''}
              </Typography>
              {direct ? (
                <Box
                  component="video"
                  src={resolveAssetUrl(url)}
                  controls
                  preload="metadata"
                  playsInline
                  sx={{
                    width: '100%',
                    maxHeight: 480,
                    borderRadius: 2,
                    bgcolor: '#000',
                    display: 'block',
                  }}
                />
              ) : embed ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    pt: '56.25%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#000',
                  }}
                >
                  <Box
                    component="iframe"
                    src={embed}
                    title={s.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </Box>
              ) : (
                <Box
                  component="a"
                  href={resolveAssetUrl(s.recordingUrl as string)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.main', fontWeight: 600 }}
                >
                  ▶ Watch the recording
                </Box>
              )}
            </Card>
          )
        })}
      </Stack>
    </PageContainer>
  )
}
